import { ErrorModal } from "@dexteel/mesf-core";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useRef, useState } from "react";
import { Ladle } from "../model/Ladle";
import { getLadles } from "../repositories/LadleRepository";

type Props = {
  value: number | null;
  onChange: (value: number) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  disabled?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  highlight?: boolean;
  disableClearable?: boolean;
};

export const LadleSelector = ({
  value,
  onChange,
  onBlur = () => {},
  onFocus = () => {},
  disabled = false,
  onOpen = () => {},
  onClose = () => {},
  highlight = false,
  disableClearable = false,
}: Props) => {
  const [ladles, setLadles] = useState<Ladle[]>([]);
  const [selectedLadle, setSelectedLadle] = useState<Ladle | null>(null);
  const filteredLadlesRef = useRef<Ladle[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const loadData = async () => {
    setIsLoading(true);
    setError("");
    try {
      const resp = await getLadles();
      if (resp.ok) {
        const rows = get(resp, "data.tables[0].rows", []) as Ladle[];
        rows.sort((a, b) => a.LadleName.localeCompare(b.LadleName));
        setLadles(rows);
      } else {
        setError(resp.message ?? "Error loading ladles");
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An error occurred while loading ladles",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();

      if (filteredLadlesRef.current.length === 1) {
        const selectedOption = filteredLadlesRef.current[0];
        setSelectedLadle(selectedOption);
        onChange(selectedOption.LadleId!);

        inputRef.current?.blur();
      }
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (value !== null && value !== undefined) {
      const ladle = ladles.find((l) => l.LadleId === value);
      setSelectedLadle(ladle || null);
    } else {
      setSelectedLadle(null);
    }
  }, [value, ladles]);

  return (
    <>
      <Autocomplete
        id="ladle-selector"
        disableClearable={disableClearable}
        clearOnEscape={!disableClearable}
        options={ladles || []}
        getOptionLabel={(option) => option.LadleName}
        filterOptions={(options, state) => {
          const filtered = options.filter((option) =>
            option.LadleName.toLowerCase().includes(
              state.inputValue.toLowerCase(),
            ),
          );
          filteredLadlesRef.current = filtered;
          return filtered;
        }}
        disabled={disabled}
        loading={isLoading}
        value={selectedLadle}
        onChange={(event, newValue) => {
          setSelectedLadle(newValue);
          if (newValue?.LadleId) {
            onChange(newValue.LadleId);
          }
        }}
        onOpen={onOpen}
        onClose={onClose}
        renderOption={(props, option, state) => (
          <li {...props}>
            <span style={{ fontWeight: state.selected ? "bold" : "normal" }}>
              {option.LadleName}
            </span>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef}
            label={"Ladle"}
            variant="outlined"
            fullWidth
            sx={
              highlight
                ? {
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "rgba(249, 248, 113, 0.35)",
                    },
                  }
                : undefined
            }
            onKeyDown={handleKeyDown}
            slotProps={{
              inputLabel: {
                sx: { fontSize: "clamp(.7rem,.8rem,1rem)" },
              },
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : (
                      params.InputProps.endAdornment
                    )}
                  </>
                ),
              },
            }}
            onBlur={onBlur}
            onFocus={onFocus}
          />
        )}
      />
      <ErrorModal
        error={error}
        onHide={() => setError("")}
        title="Error loading Ladles"
      />
    </>
  );
};
